import { Upload, notification } from "antd"
import { ArrowLeftIcon, UploadCloudIcon } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { notifyUserScopedUploaded, requestPresignedUrls } from "~/api/library"
import { sha256Hex } from "~/hooks/use-content-hash"
import useGlobalStore from "~/store/global"
import { ITokenInfo } from "~/types/global"

import {
  getMimeFromName,
  ISelectedFile,
  MEDIA_EXTS,
  normalizeFileId
} from "./types"

interface PresignedUrlResponse {
  url: string
  key: string
  s3path: string
}

interface SkippedEntry {
  requested_name: string
  existing_file: { file_id: string; s3_path: string }
}

interface UploadPaneProps {
  accept: string
  maxFiles: number
  maxFileSizeMB?: number
  onComplete: (files: ISelectedFile[]) => void
  onCancel: () => void
}

async function putToS3(url: string, file: File): Promise<void> {
  const resp = await fetch(url, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": file.type || "application/octet-stream" }
  })
  if (!resp.ok) {
    throw new Error(`S3 PUT failed (${resp.status})`)
  }
}

function toSelected(
  f: {
    file_id?: string
    id?: string
    s3_path: string
    file_name?: string
  },
  options?: {
    mime_type?: string
    transcription_status?: "pending" | "ready" | "failed"
  }
): ISelectedFile {
  const fileName = f.file_name ?? f.s3_path.split("/").pop() ?? ""
  const ext = (fileName.split(".").pop() ?? "").toLowerCase()
  // notifyUserScopedUploaded returns only the composite `id`
  // ("${tenant}|${user}|${base64}"), while the My Files list returns
  // `file_id` as the bare base64. Normalize so both shapes collapse to
  // the same key and the picker's dedup catches re-picks of the same
  // file. Without this, upload→switch-back-to-MyFiles-tab→re-click
  // produces two chips for one file.
  return {
    source: "my_files",
    file_id: normalizeFileId(f.file_id ?? f.id ?? ""),
    s3_path: f.s3_path,
    file_name: fileName,
    mime_type: options?.mime_type || getMimeFromName(fileName),
    is_media: MEDIA_EXTS.has(ext),
    transcription_status: options?.transcription_status
  }
}

export function UploadPane({
  accept,
  maxFiles,
  maxFileSizeMB,
  onComplete,
  onCancel
}: UploadPaneProps) {
  const { t } = useTranslation()
  const tokenInfo = useGlobalStore((s) => s.tokenInfo)
  const [pending, setPending] = useState(false)
  const [api, contextHolder] = notification.useNotification()

  const handleFiles = async (files: File[]) => {
    if (pending || files.length === 0 || !tokenInfo) return

    // Per-batch cap — reject the whole batch.
    if (files.length > maxFiles) {
      api.warning({
        message: t("ask_os.label_too_many_files", {
          defaultValue: "Too many files"
        }),
        description: t("ask_os.label_upload_max_files", {
          defaultValue: `Please select up to ${maxFiles} files.`,
          max: maxFiles
        })
      })
      return
    }

    // Collect ext + size rejections then emit ONE consolidated toast.
    const acceptList = accept
      .split(",")
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean)
    const maxBytes = (maxFileSizeMB ?? 10) * 1024 * 1024
    const accepted: File[] = []
    const unsupportedExt: File[] = []
    const oversized: File[] = []
    for (const f of files) {
      const ext = "." + (f.name.split(".").pop()?.toLowerCase() ?? "")
      if (!acceptList.includes(ext)) {
        unsupportedExt.push(f)
        continue
      }
      if (f.size > maxBytes) {
        oversized.push(f)
        continue
      }
      accepted.push(f)
    }

    const issues: string[] = []
    if (unsupportedExt.length) {
      issues.push(
        `${unsupportedExt.length} unsupported: ${unsupportedExt.map((f) => f.name).join(", ")}`
      )
    }
    if (oversized.length) {
      issues.push(
        `${oversized.length} over ${maxFileSizeMB ?? 10} MB: ${oversized.map((f) => f.name).join(", ")}`
      )
    }
    if (issues.length) {
      api.warning({
        message: t("ask_os.label_some_files_skipped", {
          defaultValue: "Some files skipped"
        }),
        description: issues.join(" | ")
      })
    }
    if (accepted.length === 0) return

    const trimmed = accepted
    setPending(true)
    try {
      // 1. Hash each file
      const hashed = await Promise.all(
        trimmed.map(async (file) => ({
          file,
          content_hash: await sha256Hex(file)
        }))
      )

      // 2. Presigned URL request
      const presignResp = await requestPresignedUrls(
        tokenInfo as ITokenInfo,
        hashed.map((h) => ({
          name: h.file.name,
          content_hash: h.content_hash
        }))
      )
      const data = presignResp.data?.data ?? presignResp.data ?? {}
      const presignedUrls: PresignedUrlResponse[] = data.presignedUrls ?? []
      const skipped: SkippedEntry[] = data.skipped ?? []

      // 3. Toast skipped duplicates
      skipped.forEach((s) => {
        const existingName =
          s.existing_file.s3_path.split("/").pop() ?? "(existing file)"
        api.info({
          message: "Duplicate file",
          description: `${s.requested_name} is a duplicate of ${existingName} already in your files. Using existing copy.`
        })
      })

      // 4. PUT new uploads to S3
      // Filter out skipped (dedup'd) entries so `remaining` length matches
      // `presignedUrls` length and we can correlate by index safely.
      const skippedNames = new Set(skipped.map((s) => s.requested_name))
      const remaining = hashed.filter((h) => !skippedNames.has(h.file.name))

      const uploaded: {
        s3_path: string
        content_hash: string | null
        name: string
      }[] = []
      await Promise.all(
        remaining.map(async (item, idx) => {
          const p = presignedUrls[idx]
          if (!p) return
          await putToS3(p.url, item.file)
          uploaded.push({
            s3_path: p.s3path,
            content_hash: item.content_hash,
            name: item.file.name
          })
        })
      )

      // 5. Notify backend
      let notifyFiles: {
        id?: string
        file_id?: string
        s3_path: string
        transcription_status?: "pending" | "ready" | "failed"
      }[] = []
      if (uploaded.length > 0) {
        const notifyResp = await notifyUserScopedUploaded(
          tokenInfo as ITokenInfo,
          uploaded.map((u) => ({
            s3_path: u.s3_path,
            content_hash: u.content_hash
          }))
        )
        notifyFiles =
          notifyResp.data?.data?.files ?? notifyResp.data?.files ?? []
      }

      // 6. Build ISelectedFile[]
      const result: ISelectedFile[] = [
        ...skipped.map((s) =>
          toSelected({
            file_id: s.existing_file.file_id,
            s3_path: s.existing_file.s3_path,
            file_name: s.requested_name
          })
        ),
        ...notifyFiles.map((f) =>
          toSelected(
            { file_id: f.file_id ?? f.id, s3_path: f.s3_path },
            { transcription_status: f.transcription_status }
          )
        )
      ]
      onComplete(result)
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Unknown error"
      api.error({
        message: "Upload failed",
        description: msg
      })
    } finally {
      setPending(false)
    }
  }

  return (
    <>
      {contextHolder}
      <div className="flex items-center mb-3">
        <button
          type="button"
          className="flex items-center gap-1 text-primary text-xs underline bg-transparent border-0 cursor-pointer p-0"
          onClick={onCancel}
        >
          <ArrowLeftIcon size={14} />
          Back to files
        </button>
      </div>
      <div className="border w-full pt-4 px-4 rounded-2xl border-solid border-[#d4dae3] dark:border-border">
        <Upload.Dragger
          multiple
          accept={accept}
          showUploadList={false}
          disabled={pending}
          // Use beforeUpload as the single entry point. Antd fires this
          // once per file with `fileList` carrying every file in the
          // current drop batch. Defer the actual processing until the
          // LAST file's invocation — that way handleFiles runs exactly
          // once per batch instead of once per file (Antd's onChange
          // alternative fires N times with growing fileList, producing
          // N toasts).
          beforeUpload={(file, fileList) => {
            if (file !== fileList[fileList.length - 1]) {
              return false
            }
            handleFiles(fileList)
            return false
          }}
          className="upload-dragger"
        >
          <div className="flex flex-col gap-3 justify-center items-center">
            <div className="flex gap-2 items-center">
              <UploadCloudIcon className="dark:text-dark-6 text-dark-2" />
              <p className="text-13 dark:text-dark-6 text-dark-2">
                {t("ask_os.label_drop_files_here_or")}{" "}
                <span className="font-semibold text-primary">
                  {t("ask_os.label_browse")}
                </span>
              </p>
            </div>
            <div className="border-b dark:border-border border-light-border w-full" />
            <div className="text-13 dark:text-dark-6 text-dark-2 text-center flex flex-col gap-1 py-2">
              {pending ? (
                <span className="italic">{t("ask_os.label_uploading")}</span>
              ) : (
                <>
                  <span>
                    {t("ask_os.label_upload_limits_info", {
                      size: maxFileSizeMB ?? 10,
                      count: maxFiles
                    })}
                  </span>
                  <span>
                    {t("ask_os.label_allowed_file_types_info", {
                      types: accept
                    })}
                  </span>
                </>
              )}
            </div>
          </div>
        </Upload.Dragger>
      </div>
    </>
  )
}
